<template>
  <div class="h-full overflow-hidden flex flex-col">
    <ThreadsNav show-back :show-center="false">
      <template #left>
        <div
          class="flex gap-1 text-xs md:text-sm items-center rounded-full border px-2 whitespace-nowrap py-1"
        >
          <div
            :class="[
              'rounded-full w-2 h-2',
              savingError ? 'bg-error' : isSaving ? 'bg-warning' : 'bg-success',
            ]"
          />
          <span>
            {{
              savingError
                ? t(`Message.savingFailed`)
                : t(`User.settings${isSaving ? "Unsaved" : "Saved"}`)
            }}
          </span>
        </div>
      </template>
      <template #right>
        <button
          v-if="currentMate?.id !== mateStore.userMate?.id"
          class="btn btn-xs btn-outline"
          @click="clickDeleteSelf"
        >
          删除
        </button>

        <button class="btn btn-xs btn-outline" @click="clickSprite">
          <i class="iconify pixelarticons--human-handsdown" />
          形象
        </button>
      </template>
    </ThreadsNav>

    <div class="flex-1 overflow-auto border-t">
      <BasicInfoModule
        v-if="currentMate"
        class="w-full"
        :mate-id="currentMate.id"
        :is-saving="isSaving"
        is-self-setting
        @update:mate="updateMate"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useMate } from "@/stores/mate";
import { useModule } from "@/stores/module";
import { Mate } from "@/types/entity";
import { showConfirmDialog, showLoadingToast } from "vant";
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

const route = useRoute();
const { t } = useI18n();
const mateStore = useMate();
const router = useRouter();

const savingError = ref(false);
const isSaving = ref(false);

const currentMate = ref<Mate>();
const moduleStore = useModule();
const updateMate = async (
  updateKey: keyof Mate | Array<keyof Mate>
): Promise<void> => {
  isSaving.value = true;

  const params: Partial<Mate> = {};
  if (typeof updateKey === "string") {
    params[updateKey] = currentMate.value![updateKey];
  } else if (typeof updateKey === "object") {
    updateKey.forEach((key) => {
      params[key] = currentMate.value![key];
    });
  }

  if (currentMate.value?.status === 0 && currentMate.value.name) {
    mateStore.publish(currentMate.value!.uuid!, false);
  }

  await mateStore.updateMateDetail(currentMate.value!.uuid!, params);

  isSaving.value = false;
};

const clickDeleteSelf = async () => {
  try {
    await showConfirmDialog({
      title: "确定要删除自设吗？",
      message: "删除后将无法恢复",
      confirmButtonText: "确定",
      cancelButtonText: "取消",
    });
  } catch {
    return;
  }

  const loading = showLoadingToast(`删除中...`);
  await mateStore.deleteCurrentMate(currentMate.value!);
  loading.close();
  router.back();
};

const clickSprite = () => {
  router.push({
    name: "sprite",
    query: {
      uuid: currentMate.value!.uuid,
    },
  });
};

onMounted(() => {
  const { mateUuid } = route.query;
  if (mateUuid) {
    currentMate.value = mateStore.mateMap.get(mateUuid as string)!;
    moduleStore.initModule(mateUuid as string, "mate");
  }
});
</script>
